import { useCallback } from 'react'
import styled from 'styled-components'
import { disabledCalendarDayStyles } from 'components/Common/Calendar/disabledCalendarDayStyles'
import clsx from 'clsx'
import { rem } from 'polished'

import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { AI_GLOW_SUPPRESSED_FILL_CLASS, aiGlowSuppressedFill } from 'lib/aiGlow/aiGlow'

/*
 * The grid laying these cells out (SearchDatePickerCalendar) owns the row geometry
 * with these values, so anything drawn over the grid lines up with the cells.
 */
export const SEARCH_DATE_PICKER_DAY_HEIGHT_PX = 44
export const SEARCH_DATE_PICKER_DAY_ROW_GAP_PX = 12

const Root = styled(Clickable)`
  ${disabledCalendarDayStyles}

  height: ${rem(SEARCH_DATE_PICKER_DAY_HEIGHT_PX)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: background-color 0.2s;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: ${rem(4)};

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.in-range-date {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.single-day-range {
    background-color: ${props => props.theme.palette.highlight.primary.normalActive};
    color: ${props => props.theme.palette.highlight.primary.normalContrast};
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &.center-align {
    padding-top: 0;
    align-items: center;
  }

  &:not(:disabled):not(.selected-date) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
        color: ${props => props.theme.palette.neutral.surface.normalContrast};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }

  &.${AI_GLOW_SUPPRESSED_FILL_CLASS} {
    ${aiGlowSuppressedFill}
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  day: Date
  selected: boolean
  disabled: boolean
  isInRange: boolean
  isSingleDayRange: boolean
  /** Drops the selection fill while an AI glow ring is standing in for it */
  suppressRangeHighlight?: boolean
  onClick: (day: Date) => void
}

function SearchDatePickerDay(props: Props) {
  const { day, disabled, selected, isInRange, isSingleDayRange, suppressRangeHighlight = false, onClick, ...rest } = props

  const onSelectDay = useCallback(() => {
    onClick(day)
  }, [day, onClick])

  return (
    <Root
      onClick={onSelectDay}
      disabled={disabled}
      className={clsx({
        selected,
        'in-range-date': isInRange,
        'single-day-range': isSingleDayRange,
        [AI_GLOW_SUPPRESSED_FILL_CLASS]: suppressRangeHighlight && (selected || isInRange),
      }, 'center-align')}
    >
      <BodyText {...rest} variant="medium">
        {day.getDate()}
      </BodyText>
    </Root>
  )
}

export default SearchDatePickerDay
